import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft, MessageCircle, Heart, Brain } from 'lucide-react'

const DialogueFeatures = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-pink-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-8 transition-colors duration-200 group">
          <ArrowLeft className="mr-2 w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          返回主页
        </Link>
        
        <div className="mb-16">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-violet-500 to-purple-600 rounded-3xl mb-8 shadow-2xl">
            <MessageCircle className="text-white w-12 h-12" />
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-slate-900 mb-6 tracking-tight leading-tight">
            对话功能
          </h1>
          <p className="text-2xl text-slate-600 max-w-3xl leading-relaxed">
            自然对话与情感陪伴，让交流更有温度
          </p>
        </div>
        
        <div className="space-y-8">
          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
                <MessageCircle className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">自然对话与倾听</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-8 mb-8 border border-violet-100">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Heart className="w-6 h-6 text-violet-600" />
                  功能介绍
                </h3>
                <p className="text-lg leading-relaxed mb-0">
                  支持流畅不间断日常聊天，具备倾听识别能力，完整听懂完整语句、分段说话内容，不会断句误识别，支持问答、闲聊、讲故事、科普知识等全场景日常交流。
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Brain className="w-6 h-6 text-slate-600" />
                  使用方法
                </h3>
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li>唤醒设备后，直接说出你想聊的内容，模块会完整倾听，等待你说完再进行回复</li>
                  <li>支持分多次表达想法，无需一次性说完一整段话</li>
                  <li>示例指令："给我讲一个童话故事""聊聊今天有趣的事""解释一下星空是怎么形成的"</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Heart className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">情绪识别与共情回应</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 rounded-2xl p-8 mb-8 border border-pink-100">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Heart className="w-6 h-6 text-pink-600" />
                  功能介绍
                </h3>
                <p className="text-lg leading-relaxed mb-0">
                  设备可自动感知你语音里的开心、难过、烦躁、委屈等情绪，识别到负面情绪会给出温柔安抚、共情式回复，开心时会同步积极互动，做到有温度的对话。
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8 mb-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Brain className="w-6 h-6 text-slate-600" />
                  使用方法
                </h3>
                <p className="text-lg leading-relaxed mb-0">正常和设备倾诉、抒发心情即可，无需额外指令</p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <MessageCircle className="w-6 h-6 text-slate-600" />
                  使用示例
                </h3>
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li>当你语气低落说："今天工作好累，心情很差"，设备会温柔安慰、开导你</li>
                  <li>当你兴奋分享开心事，设备会配合你的情绪一起互动祝贺</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-lg border border-slate-200 p-10 hover:shadow-xl transition-shadow duration-300">
            <div className="flex items-center gap-5 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-xl">
                <Brain className="text-white w-8 h-8" />
              </div>
              <h2 className="text-4xl font-bold text-slate-900">长对话处理</h2>
            </div>
            <div className="prose prose-xl max-w-none text-slate-700 leading-relaxed">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 mb-8 border border-blue-100">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Heart className="w-6 h-6 text-blue-600" />
                  功能介绍
                </h3>
                <p className="text-lg leading-relaxed mb-0">
                  具备长文本、复杂段落完整记忆识别能力，支持长篇故事朗读、大段内容问答、连续多轮深度对话，不会丢失前文聊天信息，多轮聊天记忆连贯不中断。
                </p>
              </div>
              <div className="bg-slate-50 rounded-2xl p-8">
                <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-3">
                  <Brain className="w-6 h-6 text-slate-600" />
                  使用场景
                </h3>
                <ul className="list-disc pl-8 space-y-3 mb-0 text-lg">
                  <li>长篇故事：完整听完上千字故事，中途提问前文内容仍能精准应答</li>
                  <li>复杂问题：分段描述复杂需求，设备整合全部信息统一回复</li>
                  <li>多轮深度聊天：连续数十轮对话，记住前面全部聊天内容</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DialogueFeatures
